import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import { useFamilyStore } from '@/hooks/useStores';
import { petsApi } from '@/api/petsWrapper';
import type { PetOwnerResponse, UpdatePetOwnerRequest, DatePrecisionType } from 'we-tree-api-client/v2';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';
import { formatDateForInput } from '@/utils/dateFormatting';

import '@/components/person/person-edit-form.css';
import '@/screens/family-member/add-pet-screen.css';
import { PET_OWNER_ROLE_OPTIONS } from '@/types/pet-owner';

interface OwnerDraft {
  role: PetOwnerResponse['role'];
  effectiveFrom: string;
  effectiveFromPrecision: DatePrecisionType;
  effectiveTo: string;
  effectiveToPrecision: DatePrecisionType;
}

function toOwnerDraft(owner: PetOwnerResponse): OwnerDraft {
  return {
    role: owner.role,
    effectiveFrom: formatDateForInput(owner.effectiveFrom),
    effectiveFromPrecision: owner.effectiveFromPrecision ?? 'day',
    effectiveTo: formatDateForInput(owner.effectiveTo),
    effectiveToPrecision: owner.effectiveToPrecision ?? 'day'
  };
}

function draftsEqual(owner: PetOwnerResponse, draft: OwnerDraft): boolean {
  const toDate = (s: string) => (s && String(s).trim() ? String(s).slice(0, 10) : '');
  return (
    owner.role === draft.role &&
    toDate(owner.effectiveFrom) === toDate(draft.effectiveFrom) &&
    (owner.effectiveFromPrecision ?? 'day') === draft.effectiveFromPrecision &&
    toDate(owner.effectiveTo ?? '') === toDate(draft.effectiveTo) &&
    (owner.effectiveToPrecision ?? 'day') === draft.effectiveToPrecision
  );
}

interface PetOwnersEditorProps {
  petFamilyMemberId: string;
  owners: PetOwnerResponse[];
  disabled?: boolean;
  onOwnersChanged: () => void;
  /** When pet is deceased, used as default for effectiveTo when adding owner (avoids date picker quirks) */
  petDeathDate?: string;
}

const PetOwnersEditor: React.FC<PetOwnersEditorProps> = observer(
  ({ petFamilyMemberId, owners, disabled, onOwnersChanged, petDeathDate }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const familyStore = useFamilyStore();
    const [error, setError] = useState<string | null>(null);
    const [savingOwnerId, setSavingOwnerId] = useState<string | null>(null);
    const [addingOwner, setAddingOwner] = useState(false);
    const [addOwnerId, setAddOwnerId] = useState('');
    const [addRole, setAddRole] = useState<'primary' | 'secondary' | 'caregiver' | ''>('');
    const [addEffectiveFrom, setAddEffectiveFrom] = useState('');
    const [addEffectiveFromPrecision, setAddEffectiveFromPrecision] = useState<DatePrecisionType>('day');
    const [addEffectiveTo, setAddEffectiveTo] = useState('');
    const [addEffectiveToPrecision, setAddEffectiveToPrecision] = useState<DatePrecisionType>('day');
    const hasInitializedFromDeathDate = useRef(false);

    // Per-owner draft state; synced from server when owners change
    const [draftByOwnerId, setDraftByOwnerId] = useState<Record<string, OwnerDraft>>({});
    useEffect(() => {
      const next: Record<string, OwnerDraft> = {};
      owners.forEach((o) => {
        next[o.id] = toOwnerDraft(o);
      });
      setDraftByOwnerId(next);
    }, [owners]);

    const setDraft = (ownerId: string, update: Partial<OwnerDraft>) => {
      setDraftByOwnerId((prev) => {
        const current = prev[ownerId] ?? toOwnerDraft(owners.find((o) => o.id === ownerId)!);
        return { ...prev, [ownerId]: { ...current, ...update } };
      });
    };

    // Default effectiveTo to pet death date when adding owner for deceased pet (avoids date picker quirks)
    useEffect(() => {
      if (petDeathDate && !hasInitializedFromDeathDate.current) {
        setAddEffectiveTo(petDeathDate);
        hasInitializedFromDeathDate.current = true;
      }
    }, [petDeathDate]);

    const ownerFamilyMemberIds = new Set(owners.map((o) => o.ownerFamilyMemberId));
    const potentialOwners = Array.from(familyStore.people.values()).filter(
      (p) => !ownerFamilyMemberIds.has(p.familyMemberId) && !p.isPlaceholder
    );

    const handleSaveOwner = async (owner: PetOwnerResponse) => {
      const draft = draftByOwnerId[owner.id];
      if (!draft || draftsEqual(owner, draft)) return;
      setError(null);
      setSavingOwnerId(owner.id);
      try {
        const toDateOnly = (s: string | undefined | null) =>
          s && String(s).trim() ? String(s).slice(0, 10) : undefined;
        const effectiveToVal =
          draft.effectiveTo.trim() === '' ? null : (toDateOnly(draft.effectiveTo) ?? draft.effectiveTo.slice(0, 10));
        const result = await petsApi.updatePetOwner(petFamilyMemberId, owner.id, {
          role: draft.role,
          effectiveFrom: toDateOnly(draft.effectiveFrom) ?? undefined,
          effectiveFromPrecision: draft.effectiveFromPrecision,
          effectiveTo: effectiveToVal,
          effectiveToPrecision: draft.effectiveToPrecision
        } as UpdatePetOwnerRequest);
        if (result.success) {
          await onOwnersChanged();
        } else {
          setError(result.error?.getUserMessage?.() ?? t('petOwners.failedUpdate'));
        }
      } catch {
        setError(t('petOwners.failedUpdateRetry'));
      } finally {
        setSavingOwnerId(null);
      }
    };

    const handleRevertOwner = (owner: PetOwnerResponse) => {
      setDraftByOwnerId((prev) => ({ ...prev, [owner.id]: toOwnerDraft(owner) }));
    };

    const handleRemoveOwner = async (petOwnerId: string) => {
      if (!window.confirm(t('petOwners.confirmRemove'))) return;
      setError(null);
      setSavingOwnerId(petOwnerId);
      try {
        const result = await petsApi.removePetOwner(petFamilyMemberId, petOwnerId);
        if (result.success) {
          await onOwnersChanged();
        } else {
          setError(result.error?.getUserMessage?.() ?? t('petOwners.failedRemove'));
        }
      } catch {
        setError(t('petOwners.failedRemoveRetry'));
      } finally {
        setSavingOwnerId(null);
      }
    };

    const handleAddOwner = async () => {
      if (!addOwnerId.trim()) return;
      setError(null);
      setAddingOwner(true);
      try {
        const effectiveTo = addEffectiveTo.trim() || petDeathDate || undefined;
        const result = await petsApi.addPetOwner(petFamilyMemberId, {
          ownerFamilyMemberId: addOwnerId,
          role: addRole || 'secondary',
          effectiveFrom: addEffectiveFrom.trim() || undefined,
          effectiveFromPrecision: addEffectiveFrom.trim() ? addEffectiveFromPrecision : undefined,
          effectiveTo,
          effectiveToPrecision: effectiveTo ? addEffectiveToPrecision : undefined
        });
        if (result.success) {
          setAddOwnerId('');
          setAddRole('');
          setAddEffectiveFrom('');
          setAddEffectiveFromPrecision('day');
          setAddEffectiveTo('');
          setAddEffectiveToPrecision('day');
          await onOwnersChanged();
        } else {
          setError(result.error?.getUserMessage?.() ?? t('petOwners.failedAdd'));
        }
      } catch {
        setError(t('petOwners.failedAddRetry'));
      } finally {
        setAddingOwner(false);
      }
    };

    return (
      <div className="add-pet-screen__owners-section">
        <h3 className="person-edit-form__section-title">{t('pet.owners')}</h3>
        {error && (
          <div className="person-relationships__error" role="alert">
            <span>{error}</span>
            <button
              type="button"
              className="person-relationships__error-dismiss"
              onClick={() => setError(null)}
              aria-label={t('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        <div className="add-pet-screen__owners-list">
          {owners.map((owner) => {
            const person = familyStore.getPerson(owner.ownerFamilyMemberId);
            const displayName =
              owner.ownerDisplayName?.trim() ||
              (person ? `${person.firstName} ${person.lastName}`.trim() : '') ||
              tCommon('unknown');
            const isSaving = savingOwnerId === owner.id;
            const draft = draftByOwnerId[owner.id] ?? toOwnerDraft(owner);
            const hasChanges = !draftsEqual(owner, draft);
            return (
              <div key={owner.id} className="add-pet-screen__owner-edit-block">
                <div className="add-pet-screen__owner-edit-row-1">
                  <Select.Root
                    value={draft.role}
                    onValueChange={(v) => setDraft(owner.id, { role: v as OwnerDraft['role'] })}
                    disabled={disabled || isSaving}
                  >
                    <Select.Trigger
                      className="form-select-trigger form-select-trigger--compact add-pet-screen__owner-role-trigger"
                      aria-label={t('petOwners.roleAria', { name: displayName })}
                    >
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                        {PET_OWNER_ROLE_OPTIONS.map((opt) => (
                          <Select.Item key={opt.value} value={opt.value} className="form-select-item">
                            <Select.ItemText>{opt.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                  <span className="add-pet-screen__owner-name">{displayName}</span>
                </div>
                <div className="add-pet-screen__owner-edit-row-2">
                  <div className="add-pet-screen__owner-dates-edit">
                    <DateFieldWithPrecision
                      id={`owner-${owner.id}-from`}
                      label={t('pet.effectiveFrom')}
                      value={draft.effectiveFrom}
                      precision={draft.effectiveFromPrecision}
                      onValueChange={(v) => setDraft(owner.id, { effectiveFrom: v.trim() })}
                      onPrecisionChange={(p) => setDraft(owner.id, { effectiveFromPrecision: p })}
                      disabled={disabled || isSaving}
                      layout="inline"
                      wrapperClassName="add-pet-screen__owner-date-field"
                    />
                    <DateFieldWithPrecision
                      id={`owner-${owner.id}-to`}
                      label={t('pet.effectiveTo')}
                      value={draft.effectiveTo}
                      precision={draft.effectiveToPrecision}
                      onValueChange={(v) => setDraft(owner.id, { effectiveTo: v.trim() })}
                      onPrecisionChange={(p) => setDraft(owner.id, { effectiveToPrecision: p })}
                      disabled={disabled || isSaving}
                      layout="inline"
                      allowClearViaInput
                      wrapperClassName="add-pet-screen__owner-date-field"
                    />
                  </div>
                  <div className="add-pet-screen__owner-actions">
                    <button
                      type="button"
                      className="btn btn--primary add-pet-screen__save-owner-button"
                      onClick={() => handleSaveOwner(owner)}
                      disabled={disabled || isSaving || !hasChanges}
                      aria-label={t('petOwners.saveAria', { name: displayName })}
                    >
                      {isSaving ? t('actions.saving') : t('actions.save')}
                    </button>
                    <button
                      type="button"
                      className="person-relationships__revert-button"
                      onClick={() => handleRevertOwner(owner)}
                      disabled={disabled || isSaving || !hasChanges}
                      aria-label={t('petOwners.revertAria', { name: displayName })}
                    >
                      {t('petOwners.revert')}
                    </button>
                    <button
                      type="button"
                      className="person-relationships__remove-button"
                      onClick={() => handleRemoveOwner(owner.id)}
                      disabled={disabled || isSaving}
                      aria-label={t('petOwners.removeAria', { name: displayName })}
                    >
                      {tCommon('remove')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {potentialOwners.length > 0 && (
          <div className="add-pet-screen__add-owner">
            <h4 className="person-edit-form__section-title add-pet-screen__add-owner-title">{t('pet.addOwner')}</h4>
            <div className="add-pet-screen__add-owner-row">
              <Select.Root
                value={addRole || undefined}
                onValueChange={(v) => setAddRole(v as 'primary' | 'secondary' | 'caregiver')}
                disabled={disabled || addingOwner}
              >
                <Select.Trigger
                  className="form-select-trigger form-select-trigger--compact add-pet-screen__role-select"
                  aria-label={t('person.relationshipType')}
                >
                  <Select.Value placeholder={t('person.relationshipType')} />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    {PET_OWNER_ROLE_OPTIONS.map((opt) => (
                      <Select.Item key={opt.value} value={opt.value} className="form-select-item">
                        <Select.ItemText>{opt.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
              <Select.Root value={addOwnerId} onValueChange={setAddOwnerId} disabled={disabled || addingOwner}>
                <Select.Trigger
                  className="form-select-trigger add-pet-screen__add-owner-select"
                  aria-label={t('petOwners.selectPersonAria')}
                >
                  <Select.Value placeholder={t('petOwners.selectPerson')} />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    {potentialOwners.map((p) => (
                      <Select.Item key={p.familyMemberId} value={p.familyMemberId} className="form-select-item">
                        <Select.ItemText>{p.fullName || tCommon('unknown')}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
              <DateFieldWithPrecision
                id="add-owner-effective-from"
                label={t('pet.effectiveFrom')}
                value={addEffectiveFrom}
                precision={addEffectiveFromPrecision}
                onValueChange={setAddEffectiveFrom}
                onPrecisionChange={setAddEffectiveFromPrecision}
                disabled={disabled || addingOwner}
                layout="inline"
                wrapperClassName="add-pet-screen__owner-date-field"
              />
              <span className="add-pet-screen__date-sep">–</span>
              <DateFieldWithPrecision
                id="add-owner-effective-to"
                label={t('pet.effectiveTo')}
                value={addEffectiveTo}
                precision={addEffectiveToPrecision}
                onValueChange={setAddEffectiveTo}
                onPrecisionChange={setAddEffectiveToPrecision}
                disabled={disabled || addingOwner}
                layout="inline"
                allowClearViaInput
                wrapperClassName="add-pet-screen__owner-date-field"
              />
              <button
                type="button"
                className="btn btn--primary add-pet-screen__add-button"
                onClick={handleAddOwner}
                disabled={disabled || addingOwner || !addOwnerId.trim() || !addRole}
              >
                {addingOwner ? t('actions.adding') : tCommon('add')}
              </button>
            </div>
          </div>
        )}

        {owners.length === 0 && potentialOwners.length === 0 && (
          <p className="person-relationships__empty-hint">{t('petOwners.empty')}</p>
        )}
      </div>
    );
  }
);

export default PetOwnersEditor;
